import { useState, useEffect, useContext, useLayoutEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { Auth } from "@aws-amplify/auth";
import SystemContext from "../../../../context/SystemContext";
import useFetch from "../../../../hooks/useFetch";
import { useProfileUpdate } from "../../../../hooks/useProfileUpdate";
import * as kApi from "../../../../constants/api";
import * as kRoutes from "../../../../constants/routes";
import { STATES, HTTP_STATUS } from "../utils/constants";
import { PROFILE_TYPES } from "../../../../constants/profile";

export const useInviteCode = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { loginInfo, setLoginInfo } = useContext(SystemContext);
  const { session } = useSelector((state) => state.session);
  const { updateProfile, currentProfile } = useProfileUpdate();

  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const openProfileModal = () => setIsProfileModalOpen(true);
  const closeProfileModal = () => setIsProfileModalOpen(false);

  // Estados do componente
  const [state, setState] = useState({
    current: STATES.LOADING,
    currentParentUser: null,
    newParentUser: null,
    rejectedUserName: "",
    initialCheckComplete: false,
    errorMessage: "",
    inviterName: "o profissional",
    unlinkInput: "",
    showUnlinkConfirm: false,
    unlinkError: "",
  });

  // Hook para chamadas de API - confirmação de código
  const [confirmCodeResult, confirmIsFetching, confirmError, fetchConfirmCode] =
    useFetch({
      autoInit: false,
    });

  // Hook para chamadas de API - desvinculação
  const [unlinkResult, unlinkIsFetching, unlinkError, fetchUnlink] = useFetch({
    autoInit: false,
  });

  // Atualizador de estado
  const updateState = (newState) => {
    setState((prevState) => ({ ...prevState, ...newState }));
  };

  // Redirecionar para o dashboard
  const redirectToDashboard = async () => {
    // 1. Limpa o código de login
    if (setLoginInfo) {
      setLoginInfo((prevState) => ({
        ...prevState,
        code: null,
      }));
    }

    // 2. Atualiza a sessão (se necessário)
    if (global.updateSession) {
      try {
        await global.updateSession();
      } catch (err) {
        console.error("Erro ao atualizar a sessão:", err);
      }
    }

    // 3. Navega para o Dashboard usando a constante de rotas correta
    // (Verifique se o import da sua constante é routesPaths ou kRoutes)
    navigate(kRoutes.R_DASHBOARD || "/painel", { replace: true });
  };

  const onGenerateNewLink = () => {
    navigate(kRoutes.R_INVITE_USER);
  };

  const handleProfileUpdated = (newProfile) => {
    if (newProfile === PROFILE_TYPES.PROFESSIONAL) {
      redirectToDashboard();
      return;
    }

    updateState({
      current: STATES.LOADING,
      initialCheckComplete: false,
    });
  };

  // Confirmar convite
  const confirmInvitation = () => {
    // Verificar se há um código na URL ou no loginInfo
    const urlParams = new URLSearchParams(location.search);
    const codeFromUrl = urlParams.get("code");
    const codeToUse = codeFromUrl || (loginInfo && loginInfo.code);

    if (!codeToUse) return;

    fetchConfirmCode({
      url: kApi.API_Q_CODE + "/" + codeToUse,
      method: "post",
      onSuccess: () => {
        updateState({ current: STATES.SUCCESS });
      },
      onError: (error) => {
        // Em caso de erro na confirmação, mostrar tela de código inválido
        updateState({
          current: STATES.INVALID_CODE,
          errorMessage: "Ops! O convite não é mais válido.",
        });
      },
    });
  };

  // Recusar convite
  const rejectInvitation = () => {
    updateState({
      current: STATES.REJECTED,
      rejectedUserName: state.newParentUser?.given_name || "",
    });
  };

  // Mostrar tela de desvinculação
  const showUnlinkScreen = () => {
    updateState({
      current: STATES.UNLINK_PROFILE,
      unlinkInput: "",
      showUnlinkConfirm: false,
    });
  };

  // Confirmar desvinculação
  const confirmUnlink = () => {
    fetchUnlink({
      url: kApi.API_Q_PARENT_THROW,
      method: "put",
      onSuccess: () => {
        updateState({ current: STATES.UNLINK_SUCCESS });
      },
      onError: () => {
        updateState({ current: STATES.UNLINK_ERROR });
      },
    });
  };

  // Cancelar confirmação de desvinculação
  const cancelUnlinkConfirm = () => {
    updateState({ showUnlinkConfirm: false });
  };

  // Função auxiliar para verificar se é o mesmo profissional (usando várias propriedades)
  const isSameProfessional = (currentParent, newParent) => {
    if (!currentParent || !newParent) return false;

    // Verificar pelo ID único (sub)
    if (
      currentParent.sub &&
      newParent.sub &&
      currentParent.sub === newParent.sub
    ) {
      return true;
    }

    // Verificar pelo nome
    if (currentParent.given_name && newParent.given_name) {
      // Verificação do nome completo, se disponível
      if (currentParent.family_name && newParent.family_name) {
        if (
          currentParent.given_name === newParent.given_name &&
          currentParent.family_name === newParent.family_name
        ) {
          return true;
        }
      }

      // Verificação só do primeiro nome
      if (currentParent.given_name === newParent.given_name) {
        return true;
      }
    }

    // Verificar pelo email
    if (
      currentParent.email &&
      newParent.email &&
      currentParent.email.toLowerCase() === newParent.email.toLowerCase()
    ) {
      return true;
    }

    return false;
  };

  // Verificar código de convite ou vínculo existente
  useLayoutEffect(() => {
    // Aguardar session ser populada pelo Redux antes de qualquer verificação
    if (!session?.my_user?.profile) return;
    if (state.initialCheckComplete) return;

    const checkInviteCode = async () => {
      // MODIFICAÇÃO: Verificar se existe código na URL (novo código)
      const urlParams = new URLSearchParams(location.search);
      const codeFromUrl = urlParams.get("code");

      // Se tiver código na URL, usar este prioritariamente,
      // caso contrário, verificar se existe no loginInfo
      const codeToCheck = codeFromUrl || (loginInfo && loginInfo.code);

      // Atualizar o loginInfo com o código da URL se existir
      if (codeFromUrl && setLoginInfo) {
        setLoginInfo((prevState) => ({
          ...prevState,
          code: codeFromUrl,
        }));
      }

      // Se não houver nenhum código para verificar
      if (!codeToCheck) {
        // Verificar se o usuário tem uma vinculação atual
        if (Auth?.user?.signInUserSession?.idToken?.jwtToken) {
          try {
            const parentResponse = await fetch(kApi.API_Q_PARENT, {
              headers: {
                Authorization: Auth.user.signInUserSession.idToken.jwtToken,
              },
            });

            if (parentResponse.ok) {
              const currentParentData = await parentResponse.json();

              // Se o usuário está vinculado, mostramos o estado NO_INVITE
              // que terá a opção de gerenciar vínculos
              if (
                currentParentData &&
                Object.keys(currentParentData).length > 0
              ) {
                updateState({
                  currentParentUser: currentParentData,
                  current: STATES.NO_INVITE,
                  initialCheckComplete: true,
                });
                return;
              }
            }
          } catch (e) {
            console.error("Erro ao verificar vínculo atual:", e);
          }
        }

        // Se não está vinculado, mostrar que não há convites
        updateState({
          current: STATES.NO_INVITE,
          errorMessage: "Não foi possível encontrar um código de convite.",
          initialCheckComplete: true,
        });
        return;
      }

      try {
        // Verificação rápida se o código é claramente inválido
        try {
          // Verifica diretamente se o código já foi usado (requisição HEAD)
          const preCheckResponse = await fetch(
            `${kApi.API_Q_CODE}/${codeToCheck}`,
            {
              method: "HEAD",
            },
          );

          // Se o código já foi usado ou não existe, mostrar tela de código inválido
          if (
            preCheckResponse.status === HTTP_STATUS.USED_CODE ||
            preCheckResponse.status === HTTP_STATUS.NOT_FOUND
          ) {
            updateState({
              current: STATES.INVALID_CODE,
              errorMessage: "Ops! O convite não é mais válido.",
              initialCheckComplete: true,
            });
            return;
          }
        } catch (e) {
          // Ignorar erros na verificação prévia e prosseguir com o fluxo normal
          console.error("Erro na verificação prévia:", e);
        }

        // Primeiro verificamos com quem o usuário já está vinculado (se houver)
        let currentParentData = null;

        if (Auth?.user?.signInUserSession?.idToken?.jwtToken) {
          try {
            const parentResponse = await fetch(kApi.API_Q_PARENT, {
              headers: {
                Authorization: Auth.user.signInUserSession.idToken.jwtToken,
              },
            });

            if (parentResponse.ok) {
              currentParentData = await parentResponse.json();
              updateState({ currentParentUser: currentParentData });
            }
          } catch (e) {
            console.error("Erro ao verificar vínculo atual:", e);
          }
        }

        // Agora verificamos o código de convite
        try {
          const inviteResponse = await fetch(
            `${kApi.API_Q_CODE}/${codeToCheck}`,
          );

          // Código válido - processamos os diferentes casos
          if (inviteResponse.ok) {
            const inviteData = await inviteResponse.json();

            // Atualizar estado com os dados do novo usuário pai
            const stateUpdates = {
              newParentUser: inviteData,
              inviterName: inviteData.given_name || "o profissional",
            };

            // Auto-convite - usuário tentando se vincular a si mesmo
            const isSelfInvite =
              (inviteData.sub &&
                session?.my_user?.sub &&
                inviteData.sub === session.my_user.sub) ||
              (inviteData.email &&
                session?.my_user?.email &&
                inviteData.email.toLowerCase() ===
                  session.my_user.email.toLowerCase());

            if (isSelfInvite) {
              updateState({
                ...stateUpdates,
                current: STATES.SELF_INVITE_BLOCKED,
                initialCheckComplete: true,
              });
              return;
            }

            // Profissional tentando se vincular a outro profissional
            if (session.my_user.profile === PROFILE_TYPES.PROFESSIONAL) {
              updateState({
                current: STATES.PROFESSIONAL_BLOCKED,
                initialCheckComplete: true,
              });
              return;
            }

            // CASO 1: Se o usuário já tem vínculo E é com o mesmo profissional do convite
            if (
              currentParentData &&
              isSameProfessional(currentParentData, inviteData)
            ) {
              updateState({
                ...stateUpdates,
                current: STATES.ALREADY_LINKED,
                errorMessage: "Você já está vinculado a este profissional.",
                initialCheckComplete: true,
              });
              return;
            }

            // CASO 2: Se tem vínculo mas com profissional diferente
            if (
              currentParentData &&
              Object.keys(currentParentData).length > 0
            ) {
              // Verificação adicional - se os nomes forem idênticos, considerar como mesmo profissional
              if (currentParentData.given_name === inviteData.given_name) {
                updateState({
                  ...stateUpdates,
                  current: STATES.ALREADY_LINKED,
                  errorMessage: "Você já está vinculado a este profissional.",
                  initialCheckComplete: true,
                });
                return;
              }

              stateUpdates.current = STATES.HAS_LINK;
            }
            // CASO 3: Se não tem vínculo ainda
            else {
              stateUpdates.current = STATES.NO_LINK;
            }

            updateState({
              ...stateUpdates,
              initialCheckComplete: true,
            });
          }
          // Erro no código - tratamos os diferentes casos de erro
          else {
            // Qualquer erro de código mostra a tela de código inválido
            updateState({
              current: STATES.INVALID_CODE,
              errorMessage: "Ops! O convite não é mais válido.",
              initialCheckComplete: true,
            });
          }
        } catch (e) {
          console.error("Erro ao verificar código de convite:", e);
          updateState({
            current: STATES.INVALID_CODE,
            errorMessage: "Ops! O convite não é mais válido.",
            initialCheckComplete: true,
          });
        }
      } finally {
        // Garante que initialCheckComplete seja true mesmo em caso de erro
        updateState({ initialCheckComplete: true });
      }
    };

    checkInviteCode();
  }, [
    loginInfo,
    location.search,
    navigate,
    setLoginInfo,
    state.initialCheckComplete,
    session,
  ]);

  return {
    state,
    updateState,
    redirectToDashboard,
    onGenerateNewLink,
    handleProfileUpdated,
    confirmInvitation,
    rejectInvitation,
    showUnlinkScreen,
    confirmUnlink,
    cancelUnlinkConfirm,
    isProfileModalOpen,
    openProfileModal,
    closeProfileModal,
    updateProfile,
    currentProfile,
    isLoading:
      state.current === STATES.LOADING ||
      !state.initialCheckComplete ||
      confirmIsFetching ||
      unlinkIsFetching,
  };
};
